import React, { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Link } from "react-router-dom"
import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Avatar,
  IconButton,
} from "@mui/material"
import HomeIcon from "@mui/icons-material/Home"
import FavoriteIcon from "@mui/icons-material/Favorite"
import { registerUser, apiError } from "../../store/actions"
import profileImg from "../../assets/images/profile-img.png"
import logoImg from "../../assets/images/logo.png"

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email")
    .required("Email is required"),
  username: Yup.string().required("Username is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
})

const MuiRegister = () => {
  const dispatch = useDispatch()

  const { user, registrationError } = useSelector((state) => ({
    user: state.Account.user,
    registrationError: state.Account.registrationError,
  }))

  // Set page title
  document.title = "Register | FlexFlow - React Admin & Dashboard Template"

  // Clear errors on mount
  useEffect(() => {
    dispatch(apiError(""))
  }, [dispatch])

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      email: "",
      username: "",
      password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(registerUser(values))
    },
  })

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.100" }}>
      {/* Home button */}
      <IconButton
        component={Link}
        to="/"
        sx={{
          position: "fixed",
          top: 16,
          right: 16,
          display: { xs: "none", sm: "flex" },
          color: "text.secondary",
        }}
      >
        <HomeIcon fontSize="large" />
      </IconButton>

      <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
        <Paper elevation={3} sx={{ overflow: "hidden" }}>
          {/* Header section */}
          <Box
            sx={{
              bgcolor: "primary.light",
              py: 4,
              px: 3,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="h5"
                color="primary.main"
                fontWeight={600}
                gutterBottom
              >
                Free Register
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get your free FlexFlow account now.
              </Typography>
            </Box>
            <Box
              component="img"
              src={profileImg}
              alt="Profile"
              sx={{
                maxWidth: 120,
                height: "auto",
              }}
            />
          </Box>

          {/* Logo */}
          <Box sx={{ px: 3, mt: -3 }}>
            <Avatar
              component={Link}
              to="/"
              sx={{
                width: 72,
                height: 72,
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              <Box
                component="img"
                src={logoImg}
                alt="Logo"
                sx={{ width: 40, height: 40 }}
              />
            </Avatar>
          </Box>

          {/* Form section */}
          <Box sx={{ p: 3 }}>
            <form onSubmit={formik.handleSubmit}>
              {user && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Register User Successfully
                </Alert>
              )}

              {registrationError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {registrationError}
                </Alert>
              )}

              <TextField
                fullWidth
                id="email"
                name="email"
                label="Email"
                type="email"
                placeholder="Enter email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                id="username"
                name="username"
                label="Username"
                type="text"
                placeholder="Enter username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.username && Boolean(formik.errors.username)
                }
                helperText={formik.touched.username && formik.errors.username}
                sx={{ mb: 3 }}
              />

              <TextField
                fullWidth
                id="password"
                name="password"
                label="Password"
                type="password"
                placeholder="Enter Password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{ py: 1.5 }}
              >
                Register
              </Button>

              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
                sx={{ mt: 3 }}
              >
                By registering you agree to the FlexFlow{" "}
                <Typography
                  component={Link}
                  to="#"
                  color="primary"
                  sx={{ textDecoration: "none" }}
                >
                  Terms of Use
                </Typography>
              </Typography>
            </form>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <Typography
              component={Link}
              to="/login"
              color="primary"
              fontWeight={500}
              sx={{ textDecoration: "none" }}
            >
              Login
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            © {new Date().getFullYear()} FlexFlow. Crafted with{" "}
            <FavoriteIcon
              sx={{
                fontSize: 14,
                color: "error.main",
                verticalAlign: "middle",
              }}
            />{" "}
            by FlexFlow
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default MuiRegister
